import React, { useEffect, useState } from "react";
import "./ProductDrawer.scss";
import {
  theme,
  Drawer,
  Typography,
  Card,
  Image,
  Button,
  Divider,
  message,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import useCard from "../../hooks/useCard";
import Counter from "../Counter/Counter";
import logo from "../../assets/CanaSaleLogo.svg";
import Utils from "../../utils";

export default function ProductDrawer({ isDrawerOpened, close, product }) {
  const { useToken } = theme;
  const { token } = useToken();
  const [messageApi, contextHolder] = message.useMessage();
  const openSuccess = () => {
    messageApi.open({
      type: "success",
      content: "Product added to cart",
    });
  };
  const openError = () => {
    messageApi.open({
      type: "error",
      content: "Failed to add product to cart",
    });
  };
  const { addToCard } = useCard();
  const shop = useSelector((state) => state?.shopDetails?.shop);
  // Shop setting "Show stock counts on shop" (CANA-639); missing key = off.
  const isCountVisible = Boolean(shop?.is_count_visible_on_shop);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [count, setCount] = useState(1);
  const [adding, setAdding] = useState(false);
  // Only cap the picker when the count is shown; otherwise keep today's
  // behaviour and let the backend reject an over-order.
  const maxCount =
    isCountVisible && selectedVariant?.count > 0
      ? selectedVariant.count
      : undefined;

  useEffect(() => {
    setCount(1);
    if (product?.variants?.length) {
      const first_variant_count_gt0 = Utils?.findVariantCountGt0(
        product?.variants
      );
      setSelectedVariant(first_variant_count_gt0);
    } else {
      setSelectedVariant(null);
    }
  }, [product]);

  useEffect(() => {
    // Switching to a weight with less stock than the picked quantity
    // must pull the quantity back down to what can actually be ordered.
    if (maxCount !== undefined) {
      setCount((prev) => (prev > maxCount ? maxCount : prev));
    }
  }, [maxCount]);

  return (
    <>
      {contextHolder}
      <Drawer
        className="productDrawer-cont"
        style={{
          backgroundColor: token?.Drawer?.colorBgDrawer,
        }}
        destroyOnHidden={true}
        placement="right"
        size={900}
        title={
          <Typography.Text
            className="productDrawer-cont__title"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            Add to Cart
          </Typography.Text>
        }
        open={isDrawerOpened}
        closeIcon={
          <CloseOutlined
            style={{
              color: token?.icons?.whiteIcon,
              fontSize: "24px",
            }}
          />
        }
        onClose={() => {
          if (typeof close === "function") {
            close();
            setCount(1);
            setSelectedVariant(null);
          }
        }}>
        <Card
          key={product?.key}
          className="productDrawer-cont__card"
          style={{
            border: "none",
            backgroundColor: token?.Card?.colorBgCard,
          }}
          styles={{
            body: {
              width: "100%",
              display: "flex",
              gap: "12px",
              padding: "8px",
            },
          }}>
          <div className="productDrawer-cont__card__imageWrapper">
            <Image
              src={product?.picture_url ? product?.picture_url : logo}
              width="56px"
              height="56px"
            />
          </div>
          <div className="productDrawer-cont__card__descriptionCont">
            <Typography.Paragraph
              className="productDrawer-cont__card__descriptionCont__text"
              style={{
                color: token?.Typography?.mainColorText,
                margin: 0,
              }}
              ellipsis={{
                rows: 2,
                tooltip: {
                  title: product?.name || "",
                },
              }}>
              {product?.name || ""}
            </Typography.Paragraph>
            {selectedVariant &&
            selectedVariant?.price &&
            selectedVariant?.sale_price &&
            Utils?.isSalePriceValid(
              selectedVariant?.sale_price,
              selectedVariant?.price
            ) ? (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}>
                <Typography.Text
                  key={selectedVariant?.id ? selectedVariant?.id : ""}
                  className="productDrawer-cont__card__descriptionCont__text"
                  style={{
                    color: token?.Typography?.colorPrimaryText,
                  }}
                  delete>
                  {selectedVariant?.price
                    ? Utils?.roundNumber(
                        count
                          ? count * selectedVariant?.price
                          : selectedVariant?.price
                      )
                    : ""}
                </Typography.Text>
                <Typography.Text
                  className="productDrawer-cont__card__descriptionCont__text"
                  style={{
                    color: token?.Typography?.mainColorText,
                  }}>
                  {selectedVariant?.price && selectedVariant?.sale_price
                    ? Utils?.roundNumber(
                        count
                          ? count * selectedVariant?.sale_price
                          : selectedVariant?.sale_price
                      )
                    : ""}
                </Typography.Text>
              </div>
            ) : (
              <Typography.Text
                className="productDrawer-cont__card__descriptionCont__text"
                style={{
                  color: token?.Typography?.mainColorText,
                }}>
                {selectedVariant?.price
                  ? Utils?.roundNumber(
                      count
                        ? selectedVariant?.price * count
                        : selectedVariant?.price
                    )
                  : ""}
              </Typography.Text>
            )}
            <div className="productDrawer-cont__card__descriptionCont__weightCont">
              <Typography.Text
                className="productDrawer-cont__card__descriptionCont__text"
                style={{
                  color: token?.Typography?.mainColorText,
                }}>
                Select Weight
              </Typography.Text>
              <div className="productDrawer-cont__card__descriptionCont__weightCont__btnWrapper">
                {product?.variants?.map((variant) => (
                  <Button
                    className="productDrawer-cont__card__descriptionCont__weightCont__btnWrapper__weight"
                    disabled={variant?.count <= 0}
                    style={{
                      borderColor:
                        variant?.id?.toString() ===
                        selectedVariant?.id?.toString()
                          ? token?.colorPrimary
                          : "",
                    }}
                    key={variant?.id}
                    onClick={() => {
                      if (variant?.count > 0) {
                        setSelectedVariant(variant);
                      }
                    }}>
                    <Typography.Text
                      className="productDrawer-cont__card__descriptionCont__text"
                      style={{
                        color:
                          variant?.id?.toString() ===
                          selectedVariant?.id?.toString()
                            ? token?.colorPrimary
                            : "",
                      }}>
                      {variant?.name && variant?.unit
                        ? Utils?.showProductDetail(variant)
                        : ""}
                    </Typography.Text>
                    {isCountVisible && Utils?.showVariantCount(variant) ? (
                      <Typography.Text
                        className="productDrawer-cont__card__descriptionCont__weightCont__btnWrapper__weight__count"
                        style={{
                          color: token?.colorTextTertiary,
                        }}>
                        {` · ${Utils?.showVariantCount(variant)}`}
                      </Typography.Text>
                    ) : null}
                  </Button>
                ))}
              </div>
            </div>
            <div className="productDrawer-cont__card__descriptionCont__countCont">
              <Typography.Text
                className="productDrawer-cont__card__descriptionCont__text"
                style={{
                  color: token?.Typography?.mainColorText,
                }}>
                Quantity
              </Typography.Text>
              <Counter
                count={count}
                max={maxCount}
                onCauntChnage={(val) => {
                  setCount(
                    maxCount !== undefined && val > maxCount ? maxCount : val
                  );
                }}
                style={{
                  padding: "0",
                }}
              />
            </div>
          </div>
        </Card>
        <Divider
          className="productDrawer-cont__divider"
          style={{ borderColor: token?.colorPrimary }}
        />
        <Button
          type="primary"
          block
          loading={adding}
          disabled={!selectedVariant}
          onClick={() => {
            const result = addToCard(selectedVariant, count, product);
            if (typeof result?.then === "function") {
              // Authenticated flow: keep the drawer open with a loading
              // button until the server confirms; only close on success.
              setAdding(true);
              result.then((res) => {
                setAdding(false);
                if (res?.type === "basket/set_item/fulfilled") {
                  close();
                  openSuccess();
                } else {
                  openError();
                }
              });
              return;
            }
            close();
            openSuccess();
          }}>
          Add to Cart
        </Button>
      </Drawer>
    </>
  );
}
